import React, { useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  InputAdornment,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { VisibilityOutlined, VisibilityOffOutlined } from "@mui/icons-material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { Trans } from "react-i18next";
import { refreshAccount } from "../accountSlice";
import FormNotificationMessage from "../../shared/components/FormNotificationMessage";
import { passwordRegex } from "../../shared/validators";
import Loader from "../../shared/components/Loader";
import { useAppDispatch, useAppSelector } from "../../shared/hooks/sharedHooks";
import { ConfirmInviteRequestForm } from "../models/AccountModels";
import { ConfirmInvite } from "../../customer/shared/models/customerModels";
import SecuredConnection from "./SecuredConnection";
import colors from "../../shared/themes/colors";
import MachineLogoIcon from "../../shared/assets/icons/MachineLogoIcon";
import {
  confirmInviteRegistration,
  confirmInviteForm,
  togglePasswordVisibility,
  toggleConfirmPasswordVisibility,
  showSuccess,
  showAccountCreated,
  setLoadFailed,
  resetState,
  setFormNotificationMessage,
} from "../confirmInviteRegistrationSlice";
import EmailSentConfirmation from "./EmailSentConfirmation";
import { handleLogin } from "../loginSlice";
import { createErrorNotification } from "../../shared/utils/errorUtils";
import { SerializedError } from "../../shared/models/errorHandlingModels";
import useTranslation from "../../shared/hooks/useTranslation";
import useDefaultRedirect from "../hooks/useDefaultRedirect";
import { AccountRoutePaths } from "../routes";
import { dt } from "../../shared/themes/redesignTokens";

const ConfirmInviteRegistration = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");

  const {
    register,
    handleSubmit,
    getValues,
    reset,
    watch,
    formState: { errors }
  } = useForm<ConfirmInviteRequestForm>();
  const { search } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { defaultRoute } = useDefaultRedirect();

  const {
    isLoading,
    isSuccess,
    isAccountCreated,
    loadFailed,
    formNotificationMessage,
    showPassword,
    showConfirmPassword,
    formData,
  } = useAppSelector((state) => state.confirmInviteRegistration);
  const { user } = useAppSelector((state) => state.account);
  const { isLoading: isLoadingLogin } = useAppSelector((state) => state.login);

  const watchedFields = watch(['firstName', 'lastName', 'password', 'confirmPassword']);

  const isFormValid = watchedFields.every(field => field && field.toString().trim() !== '');

  const handleClickShowPassword = () => {
    dispatch(togglePasswordVisibility());
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleClickShowConfirmPassword = () => {
    dispatch(toggleConfirmPasswordVisibility());
  };

  const handleMouseDownConfirmPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleConfirmRegistration = async (model: ConfirmInviteRequestForm) => {
    const { confirmPassword, name, userName, ...restModel } = model;

    const inviteModel: ConfirmInvite = {
      ...restModel,
    };

    let result;
    try {
      result = await dispatch(confirmInviteRegistration(inviteModel)).unwrap();
    } catch (error: any) {
      // Confirm FAILED -- the account was not created; show the error on the form.
      dispatch(setFormNotificationMessage(createErrorNotification(error as SerializedError).message ?? null));
      return;
    }

    // The account IS created from this point on -- never re-show the create-password form.
    try {
      await dispatch(handleLogin({
        userName: result.userName,
        password: model.password
      })).unwrap();

      dispatch(refreshAccount(null));
      dispatch(showSuccess());
    } catch {
      // SEC-REQ-4: auto-login failed AFTER the account was created. Show ONLY the
      // generic success state and route the user to the sign-in page. NEVER display
      // the login error here and NEVER retry the login (one attempt max).
      dispatch(showAccountCreated());
    }
  };

  const onSubmit: SubmitHandler<ConfirmInviteRequestForm> = (model: ConfirmInviteRequestForm) => {
    handleConfirmRegistration(model);
  };

  const loadFormData = async () => {
    const paramsString = search.replace("?", "").replaceAll("+", "%2B");
    const params = new URLSearchParams(paramsString);

    const userId = params.get("userId");
    const token = params.get("token");

    if (userId && token) {
      try {
        await dispatch(confirmInviteForm({ userId, token })).unwrap();
      } catch {
        // loadFailed + message are set by the slice's rejected reducer (F1).
      }
    } else {
      // A link without userId/token can never be completed -- treat as invalid.
      dispatch(setLoadFailed());
    }
  };

  const validateConfirmPassword = (value?: string) => {
    const { password } = getValues();
    return password === value || translate("passwordsDoNotMatch");
  };

  const handleGoToDashboard = () => {
    navigate(defaultRoute(), { replace: true });
  };

  const handleGoToSignIn = () => {
    navigate(AccountRoutePaths.signIn, { replace: true });
  };

  useEffect(() => {
    loadFormData();

    return () => {
      dispatch(resetState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (formData) {
      const paramsString = search.replace("?", "").replaceAll("+", "%2B");
      const params = new URLSearchParams(paramsString);
      const token = params.get("token");

      const formValues = {
        userId: formData.userId,
        name: formData.companyName,
        userName: formData.userName,
        firstName: formData.firstName || "",
        lastName: formData.lastName || "",
        token: token || ""
      };
      reset(formValues);
    }
  }, [formData, reset, search]);

  if (isSuccess) {
    return (
      <EmailSentConfirmation
        title={translate("allSet")}
        description={
          <Trans
            t={translate}
            i18nKey="congratulations"
            values={{ email: getValues("userName") }}
            components={{ bold: <Box component="strong" sx={{ fontWeight: 700, wordBreak: 'break-all', overflowWrap: 'break-word', maxWidth: '100%' }} /> }}
          />
        }
        buttonText={translate("goToDashboard")}
        open={true}
        onClose={handleGoToDashboard}
        hasLogo
      />
    );
  }

  if (isAccountCreated) {
    return (
      <EmailSentConfirmation
        title={translate("accountCreatedTitle")}
        description={<>{translate("accountCreatedSignIn")}</>}
        buttonText={translate("goToSignIn")}
        open={true}
        onClose={handleGoToSignIn}
        hasLogo
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.secondary.gray98,
        padding: isMobile ? "32px 16px 32px" : "48px",
      }}
    >
      <Card
        sx={{
          maxWidth: "600px",
          width: "100%",
          margin: "16px",
          display: "flex",
          flexDirection: "column",
          borderRadius: "8px",
          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          padding: isMobile ? "32px 16px 32px" : "48px",
        }}
      >
        {isLoading || isLoadingLogin || user ? (
          <Box
            sx={{
              width: '460px',
              minHeight: '420px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Loader />
          </Box>
        ) : loadFailed ? (
          <>
            <CardHeader
              title={
                <Box className="flex flex-col items-center justify-start gap-4" sx={{ paddingTop: isMobile ? "16px" : "0" }}>
                  <MachineLogoIcon />
                  <Box
                    sx={{
                      fontSize: isMobile ? "18px" : "32px",
                      fontWeight: "500",
                      lineHeight: isMobile ? "24px" : "40px",
                      letterSpacing: "-0.072px",
                      textAlign: "center",
                    }}>
                    {translate("inviteLinkInvalidTitle")}
                  </Box>
                </Box>
              }
              sx={{ pt: 0 }}
            />

            <CardContent>
              <Box sx={{ fontSize: "16px", lineHeight: "26px", color: "#6B7280", textAlign: "center" }}>
                {translate("inviteLinkInvalidDescription")}
              </Box>
            </CardContent>

            <CardActions sx={{ padding: "24px 16px 20px" }}>
              <Button
                variant="contained"
                className="w-full"
                onClick={handleGoToSignIn}
              >
                {translate("goToSignIn")}
              </Button>
            </CardActions>

            <Box className="w-full text-center">
              <SecuredConnection />
            </Box>
          </>
        ) : (
          <>
            <CardHeader
              title={
                <Box className="flex flex-col items-center justify-start gap-4" sx={{ paddingTop: isMobile ? "16px" : "0" }}>
                  <MachineLogoIcon />
                  <Box
                    sx={{
                      fontSize: isMobile ? "18px" : "36px",
                      fontWeight: "500",
                      lineHeight: isMobile ? "24px" : "42px",
                      letterSpacing: "-0.072px",
                      textAlign: "center",
                    }}>
                    {translate("createPasswordTitle")}
                  </Box>
                </Box>
              }
              sx={{ pt: 0 }}
            />

            <CardContent>
              {formNotificationMessage && (
                <Box className="mb-3">
                  <FormNotificationMessage message={formNotificationMessage} />
                </Box>
              )}
              <Box component="form" id="confirmRegistrationForm" onSubmit={handleSubmit(onSubmit)}>
                <Box component="input" type="hidden" {...register("userId" as const)} />
                <Box component="input" type="hidden" {...register("token" as const)} />

                <Box className="text-16-medium pt-4" sx={{ color: dt.blue }}>
                  {translate("company")}
                </Box>

                <Box className="flex flex-col pt-3">
                  <InputLabel>{translate("companyName")}</InputLabel>
                  <TextField
                    type="text"
                    variant="outlined"
                    className="w-full"
                    placeholder=""
                    disabled
                    {...register("name" as const)}
                  />
                </Box>

                <Box className="text-16-medium pt-8" sx={{ color: dt.blue }}>
                  {translate("personal")}
                </Box>

                <Box className="flex flex-col pt-3">
                  <InputLabel required>{translate("firstName")}</InputLabel>
                  <TextField
                    type="text"
                    variant="outlined"
                    className="w-full"
                    placeholder={translate("firstNamePlaceholder")}
                    {...register("firstName", { required: true })}
                    error={!!errors.firstName}
                    helperText={errors.firstName ? tCommon("forms.required") : ""}
                  />
                </Box>

                <Box className="flex flex-col pt-6">
                  <InputLabel required>{translate("lastName")}</InputLabel>
                  <TextField
                    type="text"
                    variant="outlined"
                    className="w-full"
                    placeholder={translate("lastNamePlaceholder")}
                    {...register("lastName", { required: true })}
                    error={!!errors.lastName}
                    helperText={errors.lastName ? tCommon("forms.required") : ""}
                  />
                </Box>

                <Box className="flex flex-col pt-6">
                  <InputLabel required>{translate("password")}</InputLabel>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    className="w-full"
                    placeholder=""
                    inputProps={{ autoComplete: 'new-password' }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    {...register("password", {
                      required: tCommon("forms.required"),
                      pattern: { value: passwordRegex, message: translate("passwordRequirements") }
                    })}
                    error={!!errors.password}
                    helperText={errors.password ? errors.password.message || translate("fieldInvalid") : ""}
                  />
                </Box>

                <Box className="flex flex-col pt-6">
                  <InputLabel required>{translate("repeatPassword")}</InputLabel>
                  <TextField
                    type={showConfirmPassword ? "text" : "password"}
                    variant="outlined"
                    className="w-full"
                    placeholder=""
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowConfirmPassword}
                            onMouseDown={handleMouseDownConfirmPassword}
                          >
                            {showConfirmPassword ? <VisibilityOutlined fontSize="small" /> : <VisibilityOffOutlined fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      )
                    }}
                    {...register("confirmPassword", { validate: validateConfirmPassword })}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword ? errors.confirmPassword.message || translate("fieldInvalid") : ""}
                  />
                </Box>
                <Box className="flex flex-col pt-6">
                  <InputLabel>{translate("yourEmail")}</InputLabel>
                  <TextField
                    fullWidth
                    type="email"
                    variant="outlined"
                    placeholder="mail@mail.com"
                    disabled
                    {...register("userName" as const)}
                  />
                </Box>
              </Box>
            </CardContent>

            <CardActions sx={{
              padding: "24px 16px 20px",
              pointerEvents: "none",
              "& button": {
                pointerEvents: "auto"
              },
              "& .MuiButton-root": {
                pointerEvents: "auto"
              }
            }}>
              <Button
                form="confirmRegistrationForm"
                type="submit"
                variant="contained"
                className="w-full"
                disabled={isLoading || !isFormValid}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!isLoading && isFormValid) {
                    handleSubmit(onSubmit)();
                  }
                }}
              >
                {translate("createAccount")}
              </Button>
            </CardActions>

            <Box className="w-full text-center">
              <SecuredConnection />
            </Box>
          </>
        )}
      </Card>
    </Box >
  );
};

export default ConfirmInviteRegistration;